import { FirebaseCachedImage } from "@components/FirebaseCachedImage";
import { FirebaseFreshImage } from "@components/FirebaseCachedImage/FirebaseFreshImage";
import { UploadIcon } from "@components/Icons/Upload";
import { FC, useRef, useState } from "react";
import { useTranslation } from "@helpers/useTranslation";
import { MAX_IMAGE_FILE_SIZE } from "@helpers/fileLimits";
import { compressImage } from "src/helpers/compress";
import { useEventId } from "src/helpers/useEvent";
import { useToast } from "src/helpers/useToast";

interface Props {
  label?: string;
  uploadLabel: string;
  reuploadLabel: string;
  onNewImage?: (file: File) => void;
  captureEnvironment?: boolean;
  allowUpload?: boolean;
  defaultImageUri?: string;
  getFreshDefault?: boolean;
  imageType?: string;
}

export const FullPicker: FC<Props> = ({
  label,
  uploadLabel,
  reuploadLabel,
  onNewImage,
  captureEnvironment,
  allowUpload,
  defaultImageUri,
  getFreshDefault,
  imageType = "default",
}) => {
  const inputEl = useRef(null);
  const imageEl = useRef(null);
  const cameraInputEl = useRef(null);
  const toast = useToast();
  const { t } = useTranslation("upload");
  const eventId = useEventId();

  const inputId = `dropzone-file-${imageType}`;
  const cameraInputId = `camera-file-${imageType}`;

  const [showPreview, setShowPreview] = useState(false);

  const handleInput = async (e) => {
    const target = e.target as HTMLInputElement;
    const files = target.files;
    if (files && files.length > 0) {
      const file = ((window as any).file = files[0]);
      if (file.size > MAX_IMAGE_FILE_SIZE) {
        toast(t("fileTooLarge"), "🤷‍♂️", "error");
        console.error(`File too large. Compressed Image. Event: ${eventId}`);
        return;
      }

      try {
        const compressed = await compressImage(file);
        setShowPreview(true);
        // PNGs preview as the original: the JPEG re-encode would paint transparent
        // logo backgrounds black. Camera photos keep the small compressed preview.
        const preview = file.type === "image/png" ? file : compressed;
        const url = window.URL.createObjectURL(preview);
        imageEl.current.src = url;
        const fileReader = new FileReader();
        fileReader.onload = function (fileReaderEvent) {
          (inputEl.current as any).src = fileReaderEvent.target.result;
        };
        fileReader.readAsDataURL(compressed);
        if (onNewImage) onNewImage(file);
      } catch (error) {
        console.error(error);
      }
    }
  };

  return (
    <div className="flex flex-col justify-center">
      <div className="my-6 text-center">
        {label ? <h2 className="mb-2 mb-4">{label}</h2> : null}

        {defaultImageUri && !showPreview && (
          <div className="mb-8 flex flex-col items-center justify-center">
            <div className="h-auto-64">
              {getFreshDefault ? (
                <FirebaseFreshImage uri={defaultImageUri} alt="" />
              ) : (
                <FirebaseCachedImage uri={defaultImageUri} alt="" />
              )}
            </div>
          </div>
        )}
        <div className="flex items-center justify-center">
          <div className="flex flex-col">
            <label
              htmlFor={inputId}
              className={`flex ${
                showPreview ? "" : "h-48 w-48 rounded-2xl border border-dashed border-secondary bg-base-200"
              } cursor-pointer flex-col items-center justify-center `}
            >
              <div
                className={`mx-10 flex flex-col items-center justify-center pt-5 pb-6 ${showPreview ? "hidden" : ""}`}
              >
                <UploadIcon className="mb-3 h-10 w-10" strokeWidth={1} />
                <p className="mb-2 text-sm">{uploadLabel}</p>
              </div>
              <div className={`flex flex-col items-center justify-center pt-5 pb-6 ${showPreview ? "" : "hidden"}`}>
                <div className="mb-4 flex justify-center">
                  <img ref={imageEl} className="max-h-96 w-screen object-scale-down" />
                </div>
                <p className="mb-2 text-xs underline">{reuploadLabel}</p>
              </div>
              <input
                onChange={handleInput}
                id={inputId}
                ref={inputEl}
                type="file"
                accept="image/*"
                capture={allowUpload ? undefined : captureEnvironment ? "environment" : "user"}
                className="hidden"
              />
            </label>
            {allowUpload && (
              <>
                <label htmlFor={cameraInputId} className="mt-3 cursor-pointer text-center text-sm underline opacity-70">
                  {t("takePhoto") || "Take photo"}
                </label>
                <input
                  onChange={handleInput}
                  id={cameraInputId}
                  ref={cameraInputEl}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
